import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { Form } from 'providers/Forms';
import { state, sequences } from 'app/pwa/app.cerebral';

import {
  Container,
  Wrapper,
  Title,
  Form as FormElement,
  Input,
  PasswordInput,
  TermsWrapper,
  TermsTextWrapper,
  TermsText,
  TermsLink,
  TermsIcon,
  RegisterButton,
  LoginWrapper,
  NewUserText,
  LoginButton,
  SpacerLine,
  OtherButton,
} from './elements';
import { useText, useLink } from 'common/hooks';
import AuthenticationError from '../common/AuthenticationError';

const deps = {
  form: state.appContainer.auth.registerForm,
  pageLoaded: state.account.pageLoaded,
  isAuthenticating: state.appContainer.auth.isAuthenticating,
  termsClicked: sequences`appContainer.termsClicked`,
  fieldChanged: sequences`appContainer.fieldChanged`,
  registerSubmitted: sequences`appContainer.auth.registerSubmitted`,
  acceptButtonClicked: sequences`appContainer.termsAcceptedClicked`,
  declineButtonClicked: sequences`appContainer.termsDeclinedClicked`,
};

export function RegisterPassword({
  form,
  pageLoaded,
  isAuthenticating,
  termsClicked,
  fieldChanged,
  registerSubmitted,
  acceptButtonClicked,
  declineButtonClicked,
  get,
}: typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const link = useLink();
  const registerForm = new Form(form, get) as any;

  return (
    <Container>
      <Wrapper>
        <Title as={'h1'}>{t('appContainer.ModLogin.register')}</Title>
        <AuthenticationError />
        <FormElement
          autoComplete={'off'}
          onSubmit={event => {
            event.preventDefault();
            registerSubmitted();
          }}
        >
          <Input
            disabled={!pageLoaded}
            errorMessage={
              !registerForm.email.isValid && !registerForm.email.isPristine
                ? t(
                    registerForm.email.errorMessage.phrase,
                    registerForm.email.errorMessage.options
                  )
                : null
            }
            name={'email'}
            inputType={'email'}
            onChange={event =>
              fieldChanged({
                field: 'appContainer.auth.registerForm.email',
                value: event.target.value,
              })
            }
            placeholder={t('appContainer.ModLogin.email')}
            value={registerForm.email.value}
          />
          <PasswordInput
            disabled={!pageLoaded}
            errorMessage={
              !registerForm.password.isValid &&
              !registerForm.password.isPristine
                ? t(
                    registerForm.password.errorMessage.phrase,
                    registerForm.password.errorMessage.options
                  )
                : null
            }
            name={'password'}
            onChange={event =>
              fieldChanged({
                field: 'appContainer.auth.registerForm.password',
                value: event.target.value,
              })
            }
            placeholder={t('appContainer.ModLogin.password')}
            value={registerForm.password.value}
          />
          <Input
            disabled={!pageLoaded}
            errorMessage={
              !registerForm.name.isValid && !registerForm.name.isPristine
                ? t(
                    registerForm.name.errorMessage.phrase,
                    registerForm.name.errorMessage.options
                  )
                : null
            }
            name={'name'}
            inputType={'text'}
            onChange={event =>
              fieldChanged({
                field: 'appContainer.auth.registerForm.name',
                value: event.target.value,
              })
            }
            placeholder={t('appContainer.ModLogin.name')}
            value={registerForm.name.value}
          />
          <TermsWrapper id={'register-consent-checkbox-text'}>
            <TermsIcon
              icon={
                registerForm.termsAccepted.value
                  ? 'icon-check_box'
                  : 'icon-check_box_outline_blank'
              }
              onClick={() =>
                registerForm.termsAccepted.value
                  ? declineButtonClicked()
                  : acceptButtonClicked()
              }
            />
            <TermsTextWrapper
              onClick={() => (pageLoaded ? termsClicked() : null)}
            >
              <TermsText>{t('appContainer.ModRegister.i_have_read')}</TermsText>
              <TermsLink>
                {t('appContainer.ModRegister.terms_of_use')}
              </TermsLink>
            </TermsTextWrapper>
          </TermsWrapper>
          <RegisterButton
            id={'register-submit-btn'}
            disabled={!registerForm.isValid || isAuthenticating || !pageLoaded}
            onClick={() => registerSubmitted()}
          >
            {t('authentication.register_account')}
          </RegisterButton>
        </FormElement>
        <OtherButton
          disabled={isAuthenticating || !pageLoaded}
          href={link('register')}
        >
          {t('authentication.use_another')}
        </OtherButton>
        <SpacerLine />
        <LoginWrapper>
          <NewUserText>{t('authentication.existing_user')}</NewUserText>
          <LoginButton href={link('login')}>
            {t('appContainer.ModLogin.login')}
          </LoginButton>
        </LoginWrapper>
      </Wrapper>
    </Container>
  );
}

export default connect(deps, RegisterPassword);
